import React from "react";

/**
 * Picture — modern-format aware image with PNG/JPG fallback.
 *
 * Usage:
 *   <Picture
 *     src="/projects/thornybush/thornybush_sunset.webp"
 *     alt=""
 *     className="w-full h-full object-cover"
 *     style={{ objectPosition: "62% 50%" }}
 *     priority // adds fetchpriority='high' + decoding='async'
 *   />
 *
 * The component looks for sibling `.avif` and `.webp` files next to the
 * original. Each path is rewritten by replacing the original extension —
 * so `/foo/bar.png` resolves to `/foo/bar.avif` and `/foo/bar.webp`.
 *
 * For the LCP image (first hero), pass `priority` — this turns on
 * `fetchpriority="high"` and disables lazy loading; everything else
 * gets `loading="lazy"` and `decoding="async"` by default.
 */

const replaceExt = (src, ext) => src.replace(/\.(png|jpg|jpeg|webp)$/i, `.${ext}`);

export default function Picture({
  src,
  alt = "",
  className = "",
  style,
  priority = false,
  imgProps = {},
  onError,
  ...rest
}) {
  const avif = replaceExt(src, "avif");
  const webp = replaceExt(src, "webp");
  const hasVariants = src !== avif; // only emit <source> tags if we actually rewrote the ext

  return (
    <picture {...rest}>
      {hasVariants && <source srcSet={avif} type="image/avif" />}
      {hasVariants && <source srcSet={webp} type="image/webp" />}
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        fetchPriority={priority ? "high" : "auto"}
        onError={onError}
        {...imgProps}
      />
    </picture>
  );
}
